/** @format */
import { inject, Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { FudisValidators, FudisValidatorFn, FudisValidatorMessage } from '@funidata/ngx-fudis';
import { TranslocoService } from '@ngneat/transloco';
import { MaxLength } from 'common-typescript/constants';

/**
 * Provides commonly used form validators (and most importantly their default error messages) which produce
 * error objects compatible with Fudis (i.e. {@link FudisValidatorMessage}). Analogous to `form-validators.ts`
 * (whose validators can't be used with Fudis forms).
 */
@Injectable({ providedIn: 'root' })
export class FudisFormValidatorsService {
    private readonly transloco = inject(TranslocoService);

    /** Simple wrapper for {@link FudisValidators.email} */
    email(message: FudisValidatorMessage = this.transloco.translate('SIS_COMPONENTS.COMMON_VALIDATION_ERRORS.EMAIL')): FudisValidatorFn {
        return FudisValidators.email(message);
    }

    /** Simple wrapper for {@link FudisValidators.maxLength} */
    maxLength(
        maxLength: MaxLength,
        message: FudisValidatorMessage = this.transloco.translate('SIS_COMPONENTS.COMMON_VALIDATION_ERRORS.MAX_LENGTH', {
            maxLength,
        }),
    ): FudisValidatorFn {
        return FudisValidators.maxLength(maxLength, message);
    }

    /** Simple wrapper for {@link FudisValidators.required} */
    required(
        message: FudisValidatorMessage = this.transloco.translate('SIS_COMPONENTS.COMMON_VALIDATION_ERRORS.REQUIRED'),
    ): FudisValidatorFn {
        return FudisValidators.required(message);
    }

    /** Simple wrapper for Angular's own {@link Validators.requiredTrue} that produces errors compatible with Fudis */
    requiredTrue(message: FudisValidatorMessage): FudisValidatorFn {
        return control => (Validators.requiredTrue(control) ? { required: { message } } : null);
    }
}
